import React, { useState, useEffect } from 'react';
import './ComposeModal.css';
import PostMetrics from '../PostMetrics/PostMetrics';
import RawResponseModal from '../RawResponseModal/RawResponseModal';
import CommentsPanel from '../CommentsPanel/CommentsPanel';
import { PLATFORM_ICONS } from '../SideIcons/SideIcons';
import { canDelete, showDeleteConfirmation } from '../../utils/postDeleteUtils';
import { showToast, showErrorToast } from '../../components/Toast/toastUtils';

const ComposeModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  editPost = null,
  canViewPlatforms = false,
  onViewPlatforms = null
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    scheduledAt: '',
    scheduledTime: '',
    status: 'draft'
  });
  const [connections, setConnections] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [media, setMedia] = useState({ url: '', type: '' });
  const [postType, setPostType] = useState('text');
  const [warningChoice, setWarningChoice] = useState(''); // 'ignore' | 'only-supported'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [error, setError] = useState('');
  const [viewingPlatforms, setViewingPlatforms] = useState(false);
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [viewingRawResponsePost, setViewingRawResponsePost] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [sourcePlatform, setSourcePlatform] = useState(null);

  // Populate form when editing a post
  useEffect(() => {
    if (editPost) {
      const scheduledDateTime = editPost.scheduled_at ? new Date(editPost.scheduled_at) : null;
      setFormData({
        title: editPost.title || '',
        content: editPost.content || '',
        scheduledAt: scheduledDateTime ? scheduledDateTime.toISOString().split('T')[0] : '',
        scheduledTime: scheduledDateTime ? scheduledDateTime.toTimeString().slice(0, 5) : '',
        status: editPost.status || 'draft'
      });

      // Extract platforms from platform_statuses dict
      const platforms = editPost.platform_statuses ? Object.keys(editPost.platform_statuses) : [];
      setSelectedPlatforms(platforms);
      setMedia({ url: editPost.media_url || '', type: editPost.media_type || '' });
    } else {
      setFormData({
        title: '',
        content: '',
        scheduledAt: '',
        scheduledTime: '',
        status: 'draft'
      });

      setSelectedPlatforms([]);
      setMedia({ url: '', type: '' });
    }

    setWarningChoice('');
    setViewingPlatforms(false);
  }, [editPost, isOpen]);

  // Determine if post is editable (ONLY draft or scheduled, or when creating a new post)
  const isEditable = !editPost || (editPost.status === 'draft' || editPost.status === 'scheduled');

  // Determine if post can be deleted (enabled for all statuses except publish_pending and delete_pending)
  const modalCanDelete = editPost ? canDelete(editPost) : false;

  const handleDeleteModalPost = async () => {
    if (!editPost || !editPost.id) return;
    
    if (!showDeleteConfirmation(editPost)) return;

    try {
      // Call the delete endpoint
      const response = await fetch(`/api/posts/${editPost.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete post');
      }
      
      // Close modal and notify parent to refresh posts list
      if (onDelete) onDelete();
      onClose();
      showToast('Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      showErrorToast(`Delete failed: ${error.message || error}`);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch('/api/platforms/connections?refresh=true', { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load platform connections'))))
      .then((data) => {
        setConnections(Array.isArray(data) ? data : []);

        // Default selection for new posts: all connected platforms
        if (!editPost && Array.isArray(data) && data.length > 0) {
          setSelectedPlatforms((prev) => (prev.length ? prev : data.map((c) => c.platform)));
        }
      })
      .catch(() => {
        setConnections([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const t = (media.type || '').toLowerCase();
    if (t.includes('video')) setPostType('video');
    else if (t.includes('image')) setPostType('image');
    else setPostType('text');
  }, [media.type]);

  const platformCaps = React.useMemo(() => {
    const map = {};
    (connections || []).forEach((c) => {
      map[c.platform] = (c.capabilities || {});
    });
    return map;
  }, [connections]);

  const unsupportedPlatforms = React.useMemo(() => {
    if (postType === 'text') return [];
    return (selectedPlatforms || []).filter((p) => {
      const caps = platformCaps[p] || {};
      const types = Array.isArray(caps.supported_post_types) ? caps.supported_post_types : ['text'];
      return !types.includes(postType);
    });
  }, [postType, selectedPlatforms, platformCaps]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleTogglePlatform = (platform) => {
    setSelectedPlatforms((prev) => {
      const set = new Set(prev);
      if (set.has(platform)) set.delete(platform);
      else set.add(platform);
      return Array.from(set);
    });
    setWarningChoice('');
  };

  const handleUpload = async (file) => {
    if (!file) return;

    setIsUploadingMedia(true);
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('file', file);

      const res = await fetch('/api/uploads', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form
      });

      if (!res.ok) {
        const msg = (await res.json().catch(() => null))?.message || 'Upload failed';
        throw new Error(msg);
      }
      const data = await res.json();
      setMedia({ url: data.media_url || data.url || '', type: data.media_type || '' });
      setWarningChoice('');
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate
    if (!formData.content.trim()) {
      setError('Post content is required');
      return;
    }

    if (isUploadingMedia) {
      setError('Please wait for media upload to finish before saving.');
      return;
    }

    // Validate scheduled time is not in the past
    if (formData.scheduledAt && formData.scheduledTime) {
      const timeStr = formData.scheduledTime || '12:00';
      const scheduledDateTime = new Date(`${formData.scheduledAt}T${timeStr}:00`);
      const now = new Date();
      
      if (scheduledDateTime <= now) {
        setError('Cannot schedule a post in the past. Please select a future date and time.');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // For non-editable posts, ignore platform selection changes
      let finalPlatforms = selectedPlatforms;
      if (!isEditable) {
        // Keep the original platforms from the editPost
        finalPlatforms = editPost.platform_statuses ? Object.keys(editPost.platform_statuses) : [];
      } else {
        // Validate platforms for non-published posts
        if (!Array.isArray(finalPlatforms) || finalPlatforms.length === 0) {
          setError('Select at least one platform');
          return;
        }

        if (unsupportedPlatforms.length > 0 && !warningChoice) {
          setError('Some selected platforms do not support this post type. Choose an option below.');
          return;
        }

        if (unsupportedPlatforms.length > 0 && warningChoice === 'only-supported') {
          finalPlatforms = finalPlatforms.filter((p) => !unsupportedPlatforms.includes(p));
          if (finalPlatforms.length === 0) {
            setError('No selected platforms support this post type.');
            return;
          }
        }
      }

      // Combine date and time if scheduling
      let scheduledDateTime = null;
      if (formData.scheduledAt) {
        const timeStr = formData.scheduledTime || '12:00';
        scheduledDateTime = `${formData.scheduledAt}T${timeStr}:00`;
      }

      // Build platform_statuses structure only if editable
      let platform_statuses = null;
      if (isEditable) {
        platform_statuses = {};
        finalPlatforms.forEach(platform => {
          platform_statuses[platform] = {
            platform: platform,
            status: scheduledDateTime ? 'pending' : 'draft'
          };
        });
      }

      const postData = {
        title: formData.title,
        content: formData.content,
        scheduled_at: scheduledDateTime,
        status: scheduledDateTime ? 'scheduled' : 'draft',
        media_url: media.url || null,
        media_type: media.type || null
      };

      // Only include platform_statuses if editable
      if (platform_statuses !== null) {
        postData.platform_statuses = platform_statuses;
      }

      await onSubmit(postData, editPost?.id);
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        scheduledAt: '',
        scheduledTime: '',
        status: 'draft'
      });
      
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const platform_statuses = editPost?.platform_statuses || {};
  const platforms = Object.keys(platform_statuses);
  const hasPlatformPosts = platforms.length > 0;

  // ALWAYS SHOW THE FUCKING POSTS UNLESS ITS DRAFT OR SCHEDULED OR PUBLISH_PENDING
  const showPlatformPostsSection = hasPlatformPosts || 
    (editPost && !['draft', 'scheduled', 'publish_pending'].includes(editPost.status));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editPost ? 'Edit Post' : 'Compose Post'}</h2>
          <div className="modal-header-actions">
            {editPost && (
              <button
                type="button"
                className="delete-btn"
                onClick={handleDeleteModalPost}
                disabled={!modalCanDelete}
                title={modalCanDelete ? 'Delete post' : 'Cannot delete post in this status'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            )}
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="compose-form">
          {error && <div className="error-message">{error}</div>}

          {!isEditable && editPost?.scheduled_at && (
            <div className="published-info">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3.5a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5z"/>
              </svg>
              <span>
                Published on {new Date(editPost.scheduled_at).toLocaleString()}
              </span>
            </div>
          )}

          <div className="form-floating">
            <input
              type="text"
              className="form-control"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter a title for your post"
              disabled={!isEditable}
            />
            <label htmlFor="title">Post Title (Optional)</label>
          </div>

          <div className="form-floating">
            <textarea
              className="form-control"
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="What's on your mind?"
              rows="4"
              required
              disabled={!isEditable}
            ></textarea>
            <label htmlFor="content">Post Content *</label>
          </div>

          {(isEditable || media.url) && (
            <div className="form-group">
              <label>Media (optional)</label>
              <div className="media-row">
                <input
                  type="file"
                  accept="image/*,video/*"
                  disabled={isUploadingMedia || !isEditable}
                  onChange={async (e) => {
                    setError('');
                    try {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      await handleUpload(file);
                    } catch (err) {
                      setError(err.message || 'Upload failed');
                    }
                  }}
                />
                {media.url ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={isUploadingMedia || !isEditable}
                    onClick={() => {
                      setMedia({ url: '', type: '' });
                      setWarningChoice('');
                    }}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              {media.url ? (
                <div className="media-prefill" style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, wordBreak: 'break-all' }}>
                    {media.url}
                  </div>
                  {(media.type || '').toLowerCase().includes('video') ? (
                    <video
                      src={media.url}
                      controls
                      style={{ marginTop: '8px', maxWidth: '100%', maxHeight: '220px', borderRadius: '8px' }}
                    />
                  ) : (
                    <img
                      src={media.url}
                      alt="Post media"
                      style={{ marginTop: '8px', maxWidth: '100%', maxHeight: '220px', borderRadius: '8px', objectFit: 'cover' }}
                    />
                  )}
                </div>
              ) : null}
              {isUploadingMedia ? (
                <div className="post-type-hint">Uploading media...</div>
              ) : null}
              <div className="post-type-hint">Post type: {postType}</div>
            </div>
          )}

          {showPlatformPostsSection && (
            <div className="form-group">
              <label>Platform Posts</label>
              <div className="platform-posts-section">
                {platforms.length > 0 ? platforms.map((platform) => {
                  const status = platform_statuses[platform];
                  const platformStatus = status?.status || 'draft';
                  const platformPostUrl = status?.platform_post_url;
                  const platformPostId = status?.platform_post_id;
                  const platformMetrics = status?.metrics || {};
                  
                  return (
                    <div key={platform} className="platform-post-item">
                      <div className="platform-post-header">
                        <span className="platform-name">{platform.toUpperCase()}</span>
                        <span className={`platform-post-status ${platformStatus}`}>
                          {platformStatus}
                        </span>
                      </div>
                      {platformPostUrl && (
                        <a
                          className="platform-post-link"
                          href={platformPostUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on platform
                        </a>
                      )}
                      {platformPostId && (
                        <div className="platform-post-id">
                          Post ID: {platformPostId}
                        </div>
                      )}
                      {/* Platform post metrics */}
                      {platformStatus === 'published' && Object.keys(platformMetrics).length > 0 && (
                        <PostMetrics 
                          metrics={platformMetrics}
                          platform={platform}
                        />
                      )}
                      {/* Raw response button for platform post status with raw_response */}
                      {status?.raw_response && (
                        <button
                          type="button"
                          className="platform-post-raw-btn"
                          onClick={() => {
                            setViewingRawResponsePost({ raw_response: status.raw_response });
                            setShowRawResponse(true);
                          }}
                        >
                          View Raw Response
                        </button>
                      )}
                      {/* Comments button for published platform posts */}
                      {platformStatus === 'published' && (
                        <button
                          type="button"
                          className="platform-post-comments-btn"
                          onClick={() => {
                            setSourcePlatform(platform);
                            setShowComments(true);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          View Comments
                        </button>
                      )}
                    </div>
                  );
                }) : (
                  <div className="platform-posts-empty">
                    {editPost?.status === 'delete_failed' ? 'Platform deletion failed. Platform posts still exist.' : 
                     editPost?.status === 'deleted' ? 'Platform posts were successfully deleted.' : 
                     'No platform posts found.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {isEditable && (
            <div className="form-group">
              <label>Platforms</label>
              <div className="platform-picker">
                {(connections || []).length === 0 ? (
                  <div className="platform-picker-empty">No connected platforms.</div>
                ) : (
                  (connections || []).map((c) => {
                    const iconConfig = PLATFORM_ICONS[c.platform] || { src: '', color: '#888', fallback: '❓' };
                    const isSelected = selectedPlatforms.includes(c.platform);
                    return (
                      <div
                        key={c.id}
                        className={`platform-icon ${isSelected ? 'selected' : ''}`}
                        onClick={() => isEditable && handleTogglePlatform(c.platform)}
                        title={`${c.platform} ${isSelected ? '(Selected)' : ''}`}
                      >
                        <img src={iconConfig.src} alt={c.platform} />
                        <span className="icon-fallback">{iconConfig.fallback}</span>
                        <span className="selected-indicator">✓</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {unsupportedPlatforms.length > 0 && (
            <div className="warning-box">
              <div className="warning-title">Some platforms don’t support {postType} posts</div>
              <div className="warning-sub">Unsupported: {unsupportedPlatforms.map((p) => p.toUpperCase()).join(', ')}</div>

              <label className="warning-choice">
                <input
                  type="radio"
                  name="warningChoice"
                  value="ignore"
                  checked={warningChoice === 'ignore'}
                  onChange={() => setWarningChoice('ignore')}
                />
                <span>Ignore warning (publish text-only on unsupported platforms)</span>
              </label>

              <label className="warning-choice">
                <input
                  type="radio"
                  name="warningChoice"
                  value="only-supported"
                  checked={warningChoice === 'only-supported'}
                  onChange={() => setWarningChoice('only-supported')}
                />
                <span>Only publish to platforms that support this type</span>
              </label>
            </div>
          )}

          {isEditable && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="scheduledAt">Schedule Date (Optional)</label>
                  <input
                    type="date"
                    id="scheduledAt"
                    name="scheduledAt"
                    value={formData.scheduledAt}
                    onChange={handleChange}
                    className="form-input"
                    min={new Date().toISOString().split('T')[0]}
                    disabled={!isEditable}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="scheduledTime">Schedule Time</label>
                  <input
                    type="time"
                    id="scheduledTime"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={handleChange}
                    className="form-input"
                    disabled={!formData.scheduledAt || !isEditable}
                  />
                </div>
              </div>

              {formData.scheduledAt && (
                <div className="schedule-info">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3.5a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                  </svg>
                  <span>
                    Post will be scheduled for {formData.scheduledAt}
                    {formData.scheduledTime && ` at ${formData.scheduledTime}`}
                  </span>
                </div>
              )}
            </>
          )}

          <div className="modal-actions">
            {isEditable && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            )}
            {isEditable && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || isUploadingMedia || !isEditable}
              >
                {isSubmitting ? (editPost ? 'Updating...' : 'Creating...') : (editPost ? 'Update Post' : (formData.scheduledAt ? 'Schedule Post' : 'Create Post'))}
              </button>
            )}
          </div>
        </form>
        
        <RawResponseModal
          isOpen={showRawResponse}
          onClose={() => { setShowRawResponse(false); setViewingRawResponsePost(null); }}
          rawResponse={viewingRawResponsePost?.raw_response}
        />

        <CommentsPanel
          isOpen={showComments}
          onClose={() => { setShowComments(false); setSourcePlatform(null); }}
          postId={editPost?.id}
          postTitle={editPost?.title || editPost?.content?.substring(0, 50)}
          availablePlatforms={platforms}
          sourcePlatform={sourcePlatform}
        />
      </div>
    </div>
  );
};

export default ComposeModal;
